import angular from 'angular'

import envRaw from './env-raw'
import MapSimple from './map-simple'
import findCssRules from './find-css-rules'
import extractRawElm from './extract-raw-elm'
import ResizeDetector from './resize-detector'

import { replaceArray } from './arrayh'
import { getElmStrShort } from './get-elm-str'
import { trim, isPosInteger } from './bits'
import { offEvent, onEventPassive } from './on-event'
import { map, uniq, includes, uniqueId, camelCase, difference } from 'lodash-es'

const ANIMATION_NAME = 'animation-name'
const ANIMATION_START = 'animationstart'

const MIN_WIDTH = 'min-width'
const MAX_WIDTH = 'max-width'
const MIN_HEIGHT = 'min-height'
const MAX_HEIGHT = 'max-height'

const ALL_ATTRS = [
  MIN_WIDTH,
  MAX_WIDTH,
  MIN_HEIGHT,
  MAX_HEIGHT
]

const ATTRS_RE = /\[(max|min)-(width|height)~="(\d+)"\]/ig
const SELECTORS_RE = /[^\s,]+\[(max|min)-(width|height)~="(\d+)"\][^\s]*/ig

angular
  .module('ggs.utils')
  .factory('cssElementQueries', cssElementQueriesProvider)

function cssElementQueriesProvider(
  $rootScope,
  $rootElement,
  domh,
  rootQs,
  addIdPrefix) {

  const rootElm = extractRawElm($rootElement)

  const animationMarker = addIdPrefix('my-css-element-queries_animation')
  const animationCss = `@keyframes ${animationMarker} { 0% { visibility: inherit; } }`
  const animationStyle = domh.createStyle(animationMarker).global(animationCss)

  const queries = []
  const elm2detector = MapSimple()
  const selector2style = MapSimple()

  $rootScope.$on('$destroy', clear)

  return {
    clear,
    update,
    applyQueries
  }

  // clear

  function clear() {
    detachStyle()
    detachHandler()
    clearQueries()
    clearSelectors()
    clearElements()
  }

  function detachStyle() {
    animationStyle.detach()
  }

  function detachHandler() {
    offEvent(rootElm, ANIMATION_START, animationStartHandler)
  }

  function clearQueries() {
    queries.length = 0
  }

  function clearSelectors() {
    const selectors = selector2style.keys()
    for (const selector of selectors) {
      releaseSelector(selector)
    }
  }

  function clearElements() {
    const elms = elm2detector.keys()
    for (const elm of elms) {
      releaseElement(elm)
      wipeAttrs(elm)
    }
  }

  // update

  function update() {
    attachStyle()
    attachHandler()
    updateQueries()
    updateSelectors()
    updateElements()
  }

  function attachStyle() {
    animationStyle.attach()
  }

  function attachHandler() {
    onEventPassive(rootElm, ANIMATION_START, animationStartHandler)
  }

  function updateQueries() {
    const actual = findQueries()
    replaceArray(queries, actual)
  }

  function updateSelectors() {
    const outSelectors = selector2style.keys()
    const actSelectors = uniq(map(queries, 'selector'))

    const delSelectors = difference(outSelectors, actSelectors)
    const newSelectors = difference(actSelectors, outSelectors)

    for (const delSelector of delSelectors) {
      releaseSelector(delSelector)
    }

    for (const newSelector of newSelectors) {
      registerSelector(newSelector)
    }
  }

  function updateElements() {
    const outElms = elm2detector.keys()
    for (const elm of outElms) {
      if (!rootElm.contains(elm) || !matchesQueries(elm)) {
        releaseElement(elm)
        wipeAttrs(elm)
      } else {
        fillAttrs(elm)
      }
    }

    const selectors = selector2style.keys()
    for (const selector of selectors) {
      if (rootElm.matches(selector) && !elm2detector.has(rootElm)) {
        registerElement(rootElm)
        fillAttrs(rootElm)
      }

      const elms = domh.find.all.notStrict(rootElm, selector)
      for (const elm of elms) {
        if (!elm2detector.has(elm)) {
          registerElement(elm)
          fillAttrs(elm)
        }
      }
    }
  }

  // handlers

  function animationStartHandler(event) {
    const elm = event.target
    let animationName = null
    if (event.animationName) {
      animationName = event.animationName
    } else {
      const styles = elm && window.getComputedStyle(elm)
      animationName = styles && styles.getPropertyValue(ANIMATION_NAME)
    }

    const isNew = !elm2detector.has(elm)
    const isNested = elm && rootElm.contains(elm)
    const isExpected = animationName && (-1 !== animationName.indexOf(animationMarker))
    const requiresSetup = isNew && isNested && isExpected

    if (requiresSetup) {
      registerElement(elm)
      fillAttrs(elm)
    }
  }

  function resizeHandler(_width, _height, $elm) {
    const elm = extractRawElm($elm)
    if (elm2detector.has(elm)) {
      fillAttrs(elm)
    }
  }

  // elements related

  function registerElement($elm) {
    const elm = extractRawElm($elm)
    const detector = ResizeDetector(elm)
    detector.pre(resizeHandler)
    elm2detector.set(elm, detector)

    if (envRaw.showsElementQueriesDebug) {
      console.debug('cssElementQueries#registerElement:', getElmStrShort(elm))
    }
  }

  function releaseElement($elm) {
    const elm = extractRawElm($elm)
    const detector = elm2detector.get(elm)

    detector.off(resizeHandler)
    elm2detector.del(elm)

    if (envRaw.showsElementQueriesDebug) {
      console.debug('cssElementQueries#releaseElement:', getElmStrShort(elm))
    }
  }

  function wipeAttrs($elm) {
    const elm = extractRawElm($elm)

    elm.removeAttribute(MIN_WIDTH)
    elm.removeAttribute(MAX_WIDTH)
    elm.removeAttribute(MIN_HEIGHT)
    elm.removeAttribute(MAX_HEIGHT)

    if (envRaw.showsElementQueriesDebug) {
      console.debug('cssElementQueries#wipeAttrs:', getElmStrShort(elm))
    }
  }

  function fillAttrs($elm) {
    const elm = extractRawElm($elm)
    const detector = elm2detector.get(elm)

    const width = detector.getWidth()
    const height = detector.getHeight()
    const matched = matchQueries(elm)
    applyQueries(elm, width, height, matched)

    if (envRaw.showsElementQueriesDebug) {
      console.debug('cssElementQueries#fillAttrs:', getElmStrShort(elm), width, height, queriesToString(matched))
    }
  }

  function applyQueries($elm, width, height, queries) {
    const elm = extractRawElm($elm)

    let minWidth = ''
    let maxWidth = ''
    let minHeight = ''
    let maxHeight = ''

    for (const { val, attr } of queries) {
      if (attr === MIN_WIDTH && width >= val) {
        minWidth += (minWidth.length === 0 ? '' : ' ') + val
      } else if (attr === MAX_WIDTH && width <= val) {
        maxWidth += (maxWidth.length === 0 ? '' : ' ') + val
      } else if (attr === MIN_HEIGHT && height >= val) {
        minHeight += (minHeight.length === 0 ? '' : ' ') + val
      } else if (attr === MAX_HEIGHT && height <= val) {
        maxHeight += (maxHeight.length === 0 ? '' : ' ') + val
      }
    }

    minWidth.length === 0
      ? elm.removeAttribute(MIN_WIDTH)
      : elm.setAttribute(MIN_WIDTH, trim(minWidth))

    maxWidth.length === 0
      ? elm.removeAttribute(MAX_WIDTH)
      : elm.setAttribute(MAX_WIDTH, trim(maxWidth))

    minHeight.length === 0
      ? elm.removeAttribute(MIN_HEIGHT)
      : elm.setAttribute(MIN_HEIGHT, trim(minHeight))

    maxHeight.length === 0
      ? elm.removeAttribute(MAX_HEIGHT)
      : elm.setAttribute(MAX_HEIGHT, trim(maxHeight))
  }

  // quires related

  function findQueries() {
    const result = []
    const cssRules = findCssRules(ATTRS_RE)
    for (const cssRule of cssRules) {
      let match = null
      while (!!(match = SELECTORS_RE.exec(cssRule.selectorText))) {
        const [fullSelector, mode, property, strVal] = match
        const selector = fullSelector.replace(ATTRS_RE, '')
        const attr = mode + '-' + property
        const val = parseInt(strVal)

        const isValid = true
          && isPosInteger(val)
          && includes(ALL_ATTRS, attr)

        if (!isValid) {
          continue
        }

        let isDuplicated = false
        for (const rule of result) {
          const local = true
            && rule.selector === selector
            && rule.attr === attr
            && rule.val === val
          if (local) {
            isDuplicated = true
            break
          }
        }

        if (!isDuplicated) {
          result.push({ selector, attr, val })
        }
      }
    }

    return result
  }

  function matchQueries($elm) {
    const elm = extractRawElm($elm)
    const matched = []
    for (const query of queries) {
      if (elm.matches(query.selector)) {
        matched.push(query)
      }
    }

    return matched
  }

  function matchesQueries($elm) {
    const elm = extractRawElm($elm)
    for (const query of queries) {
      if (elm.matches(query.selector)) {
        return true
      }
    }

    return false
  }

  function queriesToString(queries) {
    const strings = []
    for (const query of queries) {
      strings.push(`${query.selector}[${query.attr}~="${query.val}"]`)
    }
    return strings.join(' & ')
  }

  // selectors related

  function registerSelector(selector) {
    const triggerId = calcTriggerId(selector)
    const triggerCss = calcTriggerCss(selector)
    const triggerStyle = domh.createStyle(triggerId).local(triggerCss)

    triggerStyle.attach()
    selector2style.set(selector, triggerStyle)

    if (envRaw.showsElementQueriesDebug) {
      console.debug('cssElementQueries#registerSelector:', selector)
    }
  }

  function releaseSelector(selector) {
    const triggerStyle = selector2style.get(selector)

    triggerStyle.detach()
    selector2style.del(selector)

    if (envRaw.showsElementQueriesDebug) {
      console.debug('cssElementQueries#releaseSelector:', selector)
    }
  }

  function calcTriggerId(selector) {
    return uniqueId(addIdPrefix(`my-css-element-queries_trigger_${camelCase(selector)}_`))
  }

  function calcTriggerCss(selector) {
    return `${rootQs}${selector}, ${rootQs} ${selector} {\n\tanimation: 1ms ${animationMarker};\n}`
  }
}
